<template>
  <div class="step-card">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="8.5"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <path
            d="M3.8 12h16.4M12 3.5c2.2 2.35 3.35 5.18 3.35 8.5S14.2 18.15 12 20.5M12 3.5C9.8 5.85 8.65 8.68 8.65 12S9.8 18.15 12 20.5"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Онлайн-присутствие
        </span>

        <h3 class="step-title">
          Сайт компании
        </h3>
      </div>

      <span class="optional-badge">
        Необязательно
      </span>
    </div>

    <!-- Описание -->
    <p class="description">
      Добавьте ссылку на официальный сайт компании, чтобы кандидаты
      могли узнать о вас больше. Если сайта пока нет — пропустите этот шаг.
    </p>

    <!-- Поле -->
    <div class="field-container">
      <div class="field-heading">
        <label
          class="field-label"
          for="company-website"
        >
          Адрес сайта
        </label>

        <Transition name="status">
          <span
            v-if="hasValue && isValid"
            class="validation-status"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="10"
                cy="10"
                r="8"
                fill="currentColor"
              />

              <path
                d="m6.4 10.1 2.2 2.2 5-5"
                stroke="white"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            Адрес корректный
          </span>
        </Transition>
      </div>

      <div
        class="input-wrapper"
        :class="{
          'is-error': showError,
          'is-valid': hasValue && isValid
        }"
      >
        <div class="input-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="8.5"
              stroke="currentColor"
              stroke-width="1.8"
            />

            <path
              d="M3.8 12h16.4M12 3.5c2.2 2.35 3.35 5.18 3.35 8.5S14.2 18.15 12 20.5M12 3.5C9.8 5.85 8.65 8.68 8.65 12S9.8 18.15 12 20.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <input
          id="company-website"
          v-model="model.website"
          type="url"
          class="text-input"
          placeholder="company.com"
          inputmode="url"
          autocomplete="url"
          spellcheck="false"
          @input="handleInput"
          @blur="handleBlur"
        />

        <Transition name="check">
          <div
            v-if="hasValue && isValid"
            class="success-icon"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5.5 10.2 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </Transition>
      </div>

      <Transition name="message" mode="out-in">
        <div
          v-if="showError"
          key="error"
          class="field-message error-message"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <path
              d="M10 6.3v4.4M10 13.6v.1"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          Введите корректный адрес, например company.com
        </div>

        <div
          v-else
          key="hint"
          class="field-message hint-message"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              stroke="currentColor"
              stroke-width="1.5"
            />

            <path
              d="M10 9v5M10 6.2v.2"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          Можно указать адрес без https:// — мы добавим его автоматически
        </div>
      </Transition>
    </div>

    <!-- Подсказка -->
    <div class="optional-note">
      <div class="note-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M8 12.5 10.5 15 16 9.5"
            stroke="currentColor"
            stroke-width="1.9"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            stroke-width="1.6"
          />
        </svg>
      </div>

      <p>
        Этот шаг можно пропустить. Вы сможете добавить или изменить сайт
        позже в настройках компании.
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";

const model = defineModel({
  required: true
});

const emit = defineEmits([
  "valid",
  "not-valid"
]);

const isTouched = ref(false);

if (typeof model.value.website !== "string") {
  model.value.website = "";
}

const normalizedValue = computed(() =>
  model.value.website.trim()
);

const hasValue = computed(() =>
  normalizedValue.value.length > 0
);

const normalizedURL = computed(() => {
  if (!hasValue.value) {
    return "";
  }

  return /^https?:\/\//i.test(normalizedValue.value)
    ? normalizedValue.value
    : `https://${normalizedValue.value}`;
});

const isValid = computed(() => {
  if (!hasValue.value) {
    return true;
  }

  try {
    const url = new URL(normalizedURL.value);

    const hasValidProtocol =
      url.protocol === "http:" ||
      url.protocol === "https:";

    const hasValidHostname =
      url.hostname.includes(".") &&
      !url.hostname.startsWith(".") &&
      !url.hostname.endsWith(".");

    return hasValidProtocol && hasValidHostname;
  } catch {
    return false;
  }
});

const showError = computed(() =>
  isTouched.value &&
  hasValue.value &&
  !isValid.value
);

function emitValidationState() {
  if (isValid.value) {
    emit("valid");
  } else {
    emit("not-valid");
  }
}

function handleInput() {
  isTouched.value = true;
  emitValidationState();
}

function handleBlur() {
  isTouched.value = true;

  if (hasValue.value && isValid.value) {
    model.value.website = normalizedURL.value;
  } else {
    model.value.website = normalizedValue.value;
  }

  emitValidationState();
}

watch(
  () => model.value.website,
  value => {
    if ((value || "").trim().length > 0) {
      isTouched.value = true;
    }

    emitValidationState();
  },
  { immediate: true }
);
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.step-card {
  width: 100%;
  padding: 28px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.07),
      transparent 220px
    ),
    #ffffff;

  border-radius: 24px;

  animation: card-appearance 0.45s ease both;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 18px;
}

.step-icon {
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;

  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 19px;
  line-height: 1.25;
}

.optional-badge {
  flex-shrink: 0;

  padding: 7px 10px;

  color: #687385;
  background: rgba(113, 125, 145, 0.08);

  border: 1px solid rgba(113, 125, 145, 0.1);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.description {
  max-width: 680px;
  margin: 0 0 24px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.65;
}

.field-container {
  width: 100%;
  max-width: 540px;
}

.field-heading {
  min-height: 20px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 8px;
}

.field-label {
  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
  line-height: 1.3;
}

.validation-status {
  display: flex;
  align-items: center;
  gap: 5px;

  color: #27a268;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
}

.validation-status svg {
  width: 15px;
  height: 15px;
}

.input-wrapper {
  position: relative;

  width: 100%;
  min-height: 54px;

  display: flex;
  align-items: center;

  overflow: hidden;

  background: #f6f7f9;

  border: 1px solid #e1e4e9;
  border-radius: 14px;

  box-shadow:
    inset 0 1px 2px rgba(15, 23, 42, 0.025);

  transition:
    border-color 0.22s ease,
    background-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.input-wrapper:hover {
  background: #f9fafb;
  border-color: #d4d9e1;
}

.input-wrapper:focus-within {
  background: #ffffff;
  border-color: #4388f5;

  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.11),
    0 8px 24px rgba(31, 96, 205, 0.08);

  transform: translateY(-1px);
}

.input-wrapper.is-valid:not(:focus-within) {
  border-color: rgba(39, 162, 104, 0.4);
}

.input-wrapper.is-error {
  background: rgba(255, 246, 246, 0.9);
  border-color: #e15b5b;

  box-shadow:
    0 0 0 4px rgba(225, 91, 91, 0.09);
}

.input-icon {
  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-left: 16px;

  color: #939cab;

  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.input-icon svg {
  width: 20px;
  height: 20px;
}

.input-wrapper:focus-within .input-icon {
  color: #4388f5;
  transform: scale(1.05);
}

.input-wrapper.is-error .input-icon {
  color: #df5b5b;
}

.text-input {
  min-width: 0;
  width: 100%;

  padding: 16px 12px;

  color: #242933;
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 15px;
  line-height: 1.35;
}

.text-input::placeholder {
  color: #a7aeba;
  opacity: 1;
}

.success-icon {
  width: 26px;
  height: 26px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-right: 14px;

  color: #28a46b;
  background: rgba(40, 164, 107, 0.1);

  border-radius: 50%;
}

.success-icon svg {
  width: 16px;
  height: 16px;
}

.field-message {
  min-height: 20px;

  display: flex;
  align-items: flex-start;
  gap: 7px;

  margin-top: 9px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.45;
}

.field-message svg {
  width: 15px;
  height: 15px;

  flex-shrink: 0;

  margin-top: 1px;
}

.hint-message {
  color: #8a93a2;
}

.error-message {
  color: #d84e4e;
  font-weight: 600;
}

.optional-note {
  max-width: 540px;

  display: flex;
  align-items: flex-start;
  gap: 10px;

  margin-top: 24px;
  padding: 13px 14px;

  color: #667084;
  background: rgba(53, 132, 247, 0.055);

  border: 1px solid rgba(53, 132, 247, 0.1);
  border-radius: 13px;
}

.note-icon {
  width: 19px;
  height: 19px;

  flex-shrink: 0;

  color: #4388f5;
}

.note-icon svg {
  width: 19px;
  height: 19px;
}

.optional-note p {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.55;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.status-enter-active,
.status-leave-active,
.check-enter-active,
.check-leave-active,
.message-enter-active,
.message-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: translateX(5px);
}

.check-enter-from,
.check-leave-to {
  opacity: 0;
  transform: scale(0.65);
}

.message-enter-from {
  opacity: 0;
  transform: translateY(-3px);
}

.message-leave-to {
  opacity: 0;
  transform: translateY(3px);
}

/* Dark mode */

:global(html[data-theme="dark"]) .step-card {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 230px
    ),
    #1c1c1f;

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 14px 38px rgba(0, 0, 0, 0.14);
}

:global(html[data-theme="dark"]) .step-title {
  color: #f1f2f4;
}

:global(html[data-theme="dark"]) .step-label {
  color: #777f8d;
}

:global(html[data-theme="dark"]) .optional-badge {
  color: #a3aab6;
  background: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.065);
}

:global(html[data-theme="dark"]) .description {
  color: #a5a9b2;
}

:global(html[data-theme="dark"]) .field-label {
  color: #d8dae0;
}

:global(html[data-theme="dark"]) .input-wrapper {
  background: #28282c;
  border-color: #3b3b41;

  box-shadow:
    inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

:global(html[data-theme="dark"]) .input-wrapper:hover {
  background: #2b2b30;
  border-color: #484850;
}

:global(html[data-theme="dark"]) .input-wrapper:focus-within {
  background: #2d2d32;
  border-color: #548ff1;

  box-shadow:
    0 0 0 4px rgba(78, 143, 245, 0.13),
    0 9px 24px rgba(0, 0, 0, 0.14);
}

:global(html[data-theme="dark"]) .input-wrapper.is-error {
  background: rgba(91, 37, 40, 0.2);
  border-color: #d85b60;
}

:global(html[data-theme="dark"]) .text-input {
  color: #f2f2f4;
}

:global(html[data-theme="dark"]) .text-input::placeholder {
  color: #6f727b;
}

:global(html[data-theme="dark"]) .input-icon {
  color: #747985;
}

:global(html[data-theme="dark"]) .input-wrapper:focus-within .input-icon {
  color: #669cf5;
}

:global(html[data-theme="dark"]) .hint-message {
  color: #777d88;
}

:global(html[data-theme="dark"]) .optional-note {
  color: #9ba2ae;
  background: rgba(63, 137, 241, 0.07);
  border-color: rgba(74, 145, 246, 0.12);
}

/* Mobile */

@media (max-width: 640px) {
  .step-card {
    padding: 21px 18px;
    border-radius: 20px;
  }

  .step-header {
    align-items: flex-start;
  }

  .step-icon {
    width: 40px;
    height: 40px;
    border-radius: 13px;
  }

  .step-title {
    font-size: 17px;
  }

  .optional-badge {
    padding: 6px 8px;
    font-size: 9px;
  }

  .description {
    font-size: 13px;
    line-height: 1.6;
  }

  .input-wrapper {
    min-height: 52px;
  }

  .text-input {
    font-size: 16px;
  }
}

@media (max-width: 420px) {
  .optional-badge {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-card {
    animation: none;
  }

  .input-wrapper,
  .input-icon,
  .status-enter-active,
  .status-leave-active,
  .check-enter-active,
  .check-leave-active,
  .message-enter-active,
  .message-leave-active {
    transition: none;
  }

  .input-wrapper:focus-within {
    transform: none;
  }
}

html[data-theme="dark"] .step-card {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 230px
    ),
    #1c1c1e;

}

html[data-theme="dark"] .step-title {
  color: #f2f2f4;
}

html[data-theme="dark"] .step-label {
  color: #81858e;
}

html[data-theme="dark"] .optional-badge {
  color: #a3aab6;

  background-color: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.07);
}

html[data-theme="dark"] .description {
  color: #a7aab1;
}

html[data-theme="dark"] .field-label {
  color: #d8dae0;
}

html[data-theme="dark"] .validation-status {
  color: #56cb8e;
}

html[data-theme="dark"] .input-wrapper {
  background-color: #292a2e;
  border-color: #414349;

  box-shadow:
    inset 0 1px 2px
    rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .input-wrapper:hover {
  background-color: #2c2d31;
  border-color: #4a4c53;
}

html[data-theme="dark"] .input-wrapper:focus-within {
  background-color: #2d2f34;
  border-color: #5989eb;

  box-shadow:
    0 0 0 4px
    rgba(70, 126, 229, 0.15),
    0 9px 24px
    rgba(0, 0, 0, 0.18);
}

html[data-theme="dark"] .input-wrapper.is-valid:not(:focus-within) {
  border-color: rgba(61, 192, 126, 0.62);
}

html[data-theme="dark"] .input-wrapper.is-error {
  background-color: rgba(91, 37, 40, 0.22);
  border-color: #d85b60;

  box-shadow:
    0 0 0 4px
    rgba(216, 91, 96, 0.12);
}

html[data-theme="dark"] .input-icon {
  color: #7f848e;
}

html[data-theme="dark"] .input-wrapper:focus-within .input-icon {
  color: #67a2ff;
}

html[data-theme="dark"] .input-wrapper.is-error .input-icon {
  color: #ff858b;
}

html[data-theme="dark"] .text-input {
  color: #f2f2f4;
  caret-color: #67a2ff;
}

html[data-theme="dark"] .text-input::placeholder {
  color: #70747c;
}

html[data-theme="dark"] .success-icon {
  color: #51c989;
  background-color: rgba(61, 192, 126, 0.14);
}

html[data-theme="dark"] .hint-message {
  color: #7f838b;
}

html[data-theme="dark"] .error-message {
  color: #ff8b94;
}

html[data-theme="dark"] .optional-note {
  color: #9ba2ae;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.10),
      rgba(83, 104, 232, 0.06)
    );

}

html[data-theme="dark"] .note-icon {
  color: #6ba5ff;
}

</style>